// 설정 시트 — 우상단 톱니바퀴에서 열리는 메뉴.
//
// 로그인 상태에 따라 세 가지 모드:
//  - admin: 회원 관리 / 정기모임 템플릿 / 내 프로필 / 로그아웃
//  - member: 내 프로필 / 로그아웃
//  - guest: 관리자 비밀번호 폼 + 회원 PIN 로그인 링크
//
// 클럽 카드 + 페어매치 about 섹션은 모든 모드 공통 하단.
// 로그아웃 후의 리렌더는 호출자가 주입한 rerender 콜백으로.

import { Fragment } from "preact";
import { useState } from "preact/hooks";
import { api } from "../api";
import { adminRoleLabel, currentDisplayName, isAdmin, isMember } from "../auth";
import { openEventTemplatesSheet } from "../events/templates";
import { navigate } from "../routes";
import { BRAND, persist, state } from "../state";
import { Icon } from "./components/Icon";
import { mountPreact } from "./preact-mount";
import { closeSheet, openSheet, toast } from "./sheet";

export interface OpenSettingsSheetOpts {
  rerender: () => void;
}

interface MenuItemProps {
  icon: string;
  label: string;
  hint?: string;
  variant?: "accent" | "danger";
  trailing?: boolean;
  onClick: () => void;
}

function MenuItem({ icon, label, hint, variant, trailing = true, onClick }: MenuItemProps) {
  const cls = [
    "menu__item",
    hint ? "menu__item--stack" : null,
    variant === "accent" ? "menu__item--accent" : null,
    variant === "danger" ? "menu__item--danger" : null,
  ].filter(Boolean).join(" ");
  return (
    <button class={cls} type="button" onClick={onClick}>
      <div class="menu__icon"><Icon name={icon} size={18} /></div>
      {hint ? (
        <div class="menu__body">
          <div class="menu__label">{label}</div>
          <div class="menu__hint">{hint}</div>
        </div>
      ) : (
        <div class="menu__label">{label}</div>
      )}
      {trailing && <div class="list__trail"><Icon name="chevron" size={16} /></div>}
    </button>
  );
}

function AdminMenu({ rerender }: { rerender: () => void }) {
  const adminLabel = adminRoleLabel() || "관리자";
  return (
    <Fragment>
      <div class="section">
        <div class="section__title"><h3>{adminLabel} 모드 ON</h3></div>
        <div class="menu">
          <MenuItem
            icon="users"
            label="회원 관리"
            variant="accent"
            onClick={() => { closeSheet(); navigate({ name: "members" }); }}
          />
          <MenuItem
            icon="calendar"
            label="정기모임 템플릿"
            hint="매주 반복되는 일정 설정을 저장해두세요"
            onClick={() => openEventTemplatesSheet()}
          />
        </div>
      </div>
      {state.session?.actor?.member_id && (
        <div class="section">
          <div class="menu">
            <MenuItem
              icon="bar-chart"
              label="내 프로필"
              onClick={() => { closeSheet(); navigate({ name: "my_record" }); }}
            />
          </div>
        </div>
      )}
      <div class="section">
        <div class="menu">
          <MenuItem
            icon="logout"
            label="로그아웃"
            variant="danger"
            trailing={false}
            onClick={() => {
              state.session = null;
              persist();
              closeSheet();
              toast("로그아웃했어요");
              rerender();
            }}
          />
        </div>
      </div>
    </Fragment>
  );
}

function MemberMenu({ rerender }: { rerender: () => void }) {
  const loginName = currentDisplayName() || "회원";
  return (
    <div class="section">
      <div class="section__title"><h3>{loginName}님으로 로그인됨</h3></div>
      <p class="muted small" style="margin:-6px 0 10px">
        로그인 이름: "{loginName}" — 다음 로그인 시 이대로 입력
      </p>
      <div class="menu">
        <MenuItem
          icon="bar-chart"
          label="내 프로필"
          variant="accent"
          onClick={() => { closeSheet(); navigate({ name: "my_record" }); }}
        />
        <MenuItem
          icon="logout"
          label="로그아웃"
          variant="danger"
          trailing={false}
          onClick={() => {
            state.session = null;
            persist();
            closeSheet();
            toast("로그아웃했어요");
            rerender();
          }}
        />
      </div>
    </div>
  );
}

function GuestAdminLogin({ rerender }: { rerender: () => void }) {
  const [pw, setPw] = useState("");

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!pw) { toast("비밀번호를 입력해주세요"); return; }
    try {
      const res = await api(`/api/v1/clubs/${state.config.clubSlug}/admin-login`, {
        method: "POST",
        body: { password: pw },
      });
      state.session = res.auth;
      if (res.club) state.club = res.club;
      persist();
      closeSheet();
      toast("관리자 모드로 들어왔어요");
      rerender();
    } catch (err: any) {
      if (err.status === 401) toast("비밀번호가 올바르지 않아요");
      else toast(err.message);
    }
  }

  return (
    <Fragment>
      <div class="section">
        <div class="section__title"><h3>관리자 모드</h3></div>
        <form class="form" onSubmit={onSubmit}>
          <div class="field">
            <span class="field__label">관리자 비밀번호</span>
            <input
              class="input"
              type="password"
              inputmode="numeric"
              placeholder="관리자 비밀번호"
              autocomplete="off"
              value={pw}
              onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)}
            />
            <span class="field__hint">운영진만 사용하는 모드입니다</span>
          </div>
          <button class="btn btn--primary btn--block" type="submit">
            <Icon name="login" size={16} />
            관리자 모드 들어가기
          </button>
        </form>
      </div>
      <p class="brand-hint">
        <span>🎾 {BRAND.name}로 운영되는 클럽 앱</span>
      </p>
      <div class="section">
        <div class="section__title"><h3>회원 로그인</h3></div>
        <div class="menu">
          <MenuItem
            icon="login"
            label="회원 PIN으로 로그인"
            onClick={() => { closeSheet(); navigate({ name: "member_login" }); }}
          />
        </div>
        <p class="menu__hint">운영진이 보내준 초대 링크를 먼저 열어 PIN을 설정해주세요.</p>
      </div>
    </Fragment>
  );
}

function ClubSection() {
  const label = state.club?.name || state.config.clubSlug;
  const hint = state.config.clubSlug;
  return (
    <div class="section">
      <div class="section__title"><h3>클럽</h3></div>
      <div class="menu">
        {isAdmin() ? (
          <button
            class="menu__item menu__item--stack"
            type="button"
            onClick={() => { closeSheet(); navigate({ name: "club_settings" }); }}
          >
            <div class="menu__icon"><Icon name="trophy" size={18} /></div>
            <div class="menu__body">
              <div class="menu__label">{label}</div>
              <div class="menu__hint">{hint}</div>
            </div>
            <div class="list__trail"><Icon name="chevron" size={16} /></div>
          </button>
        ) : (
          <div class="menu__item menu__item--stack">
            <div class="menu__icon"><Icon name="trophy" size={18} /></div>
            <div class="menu__body">
              <div class="menu__label">{label}</div>
              <div class="menu__hint">{hint}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BrandAboutSection() {
  const mailto = `mailto:${BRAND.contactEmail}` +
    `?subject=${encodeURIComponent("[페어매치] 문의/피드백")}` +
    `&body=${encodeURIComponent(
      `안녕하세요, 페어매치 팀!\n\n` +
      `(클럽: ${state.club?.name || state.config.clubSlug || "-"})\n\n` +
      `아래에 남기고 싶은 말을 적어주세요:\n`,
    )}`;
  return (
    <div class="section about-section">
      <div class="menu">
        <a class="menu__item menu__item--link menu__item--stack" href={mailto}>
          <div class="menu__icon"><Icon name="share" size={18} /></div>
          <div class="menu__body">
            <div class="menu__label">페어매치에게 물어보기</div>
            <div class="menu__hint">{BRAND.tagline}</div>
          </div>
          <div class="list__trail"><Icon name="chevron" size={16} /></div>
        </a>
      </div>
      <p class="about-version">🎾 {BRAND.name} · {BRAND.tagline}</p>
    </div>
  );
}

function SettingsSheet({ rerender }: { rerender: () => void }) {
  return (
    <div class="stack">
      {isAdmin() ? <AdminMenu rerender={rerender} />
        : isMember() ? <MemberMenu rerender={rerender} />
        : <GuestAdminLogin rerender={rerender} />}
      <ClubSection />
      <BrandAboutSection />
    </div>
  );
}

export function openSettingsSheet(opts: OpenSettingsSheetOpts): void {
  openSheet("설정", mountPreact(<SettingsSheet rerender={opts.rerender} />));
}
